// Angular Files
import { CommonModule } from '@angular/common';
import {
    Component,
    NgModule,
    Input,
    ChangeDetectorRef,
} from '@angular/core';
import {
    UntypedFormControl,
    UntypedFormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';

// Angular Material Files
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule } from '@angular/material/legacy-select';

// Other External Files
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { Subscription } from 'rxjs';

// Payment Integration Files
import { BillingDetails } from 'apps/public-portal/src/app/payment-integrations/base/models';

// Teller Online Files
import { StateOrProvince, Country, COUNTRIES } from 'apps/public-portal/src/app/shared/constants';

// Teller Online Library Files
import { TellerOnlineIconsModule } from 'teller-online-libraries/icons';
import { TellerOnlineAppService } from 'teller-online-libraries/core';
import { TellerOnlineValidationService } from 'teller-online-libraries/shared';

@Component({
    selector: 'app-billing-info',
    templateUrl: './billing-info.component.html',
    styleUrls: ['./billing-info.component.scss'],
    host: {
        class: 'billing-info'
    }
})
export class BillingInfoComponent {
    // Declare @Input variables
    @Input() public set billingInfo(value: BillingDetails) {
        this._billingInfo = value;
        this.cdr.detectChanges();
    }

    public get billingInfo() {
        return this._billingInfo;
    }

    private _billingInfo: BillingDetails;

    @Input() public requiredFields: BillingInfoFields[] = [];

    @Input() public disabledFields: BillingInfoFields[] = [];

    @Input() public forEdit: boolean = false;

    @Input() public set formGroup(value: UntypedFormGroup) {
        if (this._formChangeSubscription) this._formChangeSubscription.unsubscribe();

        this._formGroup = value;

        if (!this._billingInfo) {
            if (this.appService.debug) {
                throw new Error('Billing info binding must be loaded before formGroup binding');
            }
        }

        this.stateOrProvinceList = StateOrProvince.GetByCountryCode(
            this.billingInfo.addressCountry?.code
        );

        // Add the billing info fields to the form, defaulted to the billing info values
        const fields = this.BILLING_INFO_FIELDS;
        const enums = BillingInfoFields;
        const data = this.billingInfo;
        this._addControl(fields.email, enums.email, data.email);
        this._addControl(fields.phone, enums.phone, data.phone);
        this._addControl(fields.addressLine1, enums.addressLine1, data.addressLine1);
        this._addControl(fields.addressLine2, enums.addressLine2, data.addressLine2);
        this._addControl(fields.addressCity, enums.addressCity, data.addressCity);
        this._addControl(fields.addressState, enums.addressState, data.addressState);
        this._addControl(fields.addressRegion, enums.addressRegion, data.addressRegion);
        this._addControl(fields.addressCountry, enums.addressCountry, data.addressCountry);
        this._addControl(fields.addressZip, enums.addressZip, data.addressZip);

        // Anytime one of the form controls changes, update the billing info
        this._formChangeSubscription = this._formGroup.valueChanges.subscribe(formValue => {
            const get = (name: string) => this._getBillingInfoFieldValue(formValue, name);

            this.billingInfo.email = get(fields.email);
            this.billingInfo.phone = get(fields.phone);
            this.billingInfo.addressCity = get(fields.addressCity);
            this.billingInfo.addressLine1 = get(fields.addressLine1);
            this.billingInfo.addressLine2 = get(fields.addressLine2);
            this.billingInfo.addressZip = get(fields.addressZip);
            this.billingInfo.addressRegion = get(fields.addressRegion);

            // Unset the state if the country was changed
            const newCountry = get(fields.addressCountry);
            if (this.billingInfo.addressCountry?.code !== newCountry?.code) {
                this.billingInfo.addressCountry = newCountry;

                // Toggle validation rules on fields that are switching visibility
                if (Country.HasRegionList(this.billingInfo.addressCountry?.code)) {
                    // Country has a list of state/provinces so enable the validator
                    // for the state dropdown field
                    if (this.requiredFields.includes(BillingInfoFields.addressState)) {
                        this._formGroup.controls[fields.addressState]
                            .setValidators(Validators.required);
                    }

                    // Country has a list of state/provinces so disable the
                    // validator for the region field since it will be hidden.
                    if (this.requiredFields.includes(BillingInfoFields.addressRegion)) {
                        this._formGroup.controls[fields.addressRegion].clearValidators();
                    }
                } else {
                    // Country does not have a list of state/provinces so disable
                    // the validator for the state dropdown since it will be hidden.
                    if (this.requiredFields.includes(BillingInfoFields.addressState)) {
                        this._formGroup.controls[fields.addressState].clearValidators();
                    }

                    // Country does not have a list of state/provinces so enable
                    // the validator for the region field.
                    if (this.requiredFields.includes(BillingInfoFields.addressRegion)) {
                        this._formGroup.controls[fields.addressRegion]
                            .setValidators(Validators.required);
                    }
                }

                // Force the form fields to update. This will trigger the
                // subscription event which will update the backing fields.
                this._formGroup.controls[fields.addressState].setValue(undefined);
                this._formGroup.controls[fields.addressRegion].setValue(undefined);
            } else {
                this.billingInfo.addressState = get(fields.addressState);
                this.billingInfo.addressRegion = get(fields.addressRegion);
            }

            this.stateOrProvinceList = StateOrProvince.GetByCountryCode(
                this.billingInfo.addressCountry?.code
            );
        });

        this.cdr.detectChanges();
    }

    public get formGroup() {
        return this._formGroup;
    }

    private _formGroup: UntypedFormGroup;

    // Declare @Output variables

    // Public variables
    public countryList: Country[] = COUNTRIES;

    public stateOrProvinceList: StateOrProvince[];

    public BILLING_INFO_FIELDS = {
        email: 'Email',
        phone: 'Phone Number',
        addressCity: 'City',
        addressLine1: 'Address Line 1',
        addressLine2: 'Address Line 2',
        addressState: 'State',
        addressRegion: 'Region',
        addressCountry: 'Country',
        addressZip: 'Zip Code'
    };

    public BillingInfoFields = BillingInfoFields;

    public get stateLabel() {
        const code = this.billingInfo.addressCountry?.code;
        if (code === 'US') return 'State';
        if (code === 'CA') return 'Province';
        return 'Region';
    }

    public get zipLabel() {
        return this.billingInfo.addressCountry?.code === 'US'
            ? 'Zip Code'
            : 'Postal Code';
    }

    public get zipMask() {
        const code = this.billingInfo.addressCountry?.code;
        if (code === 'US') return '00000';
        if (code === 'CA') return 'S0S 0S0';
        return '';
    }

    public get zipInputMode() {
        return this.billingInfo.addressCountry?.code === 'US' ? 'numeric' : '';
    }

    public get phoneMask() {
        const code = this.billingInfo.addressCountry?.code;
        return this.validationService.getPhoneNumberMask(code === 'US' || code === 'CA');
    }

    // Private variables

    // Subscriptions
    private _formChangeSubscription: Subscription;

    constructor(
        public validationService: TellerOnlineValidationService,
        private appService: TellerOnlineAppService,
        private cdr: ChangeDetectorRef
    ) {
    }

    public countryHasRegionList(countryCode: string) {
        return Country.HasRegionList(countryCode);
    }

    private _addControl(fieldName: string, fieldEnum: BillingInfoFields, fieldValue: any) {
        const control = new UntypedFormControl({
            value: [fieldValue],
            disabled: this.disabledFields.includes(fieldEnum)
        });
        control.setValue(fieldValue);
        this._formGroup.addControl(fieldName, control);
    }

    private _getBillingInfoFieldValue(formValue: any, fieldName: string): any {
        let field = '';
        field = formValue[fieldName];

        // If the field is undefined, it may be missing from the form values.
        // This will happen for disabled fields. In this case, we need to find
        // the value from the controls collection instead.
        if (field === undefined) {
            field = this._formGroup.controls[fieldName].value;
        }

        return field;
    }
}
@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatLegacyFormFieldModule,
        MatLegacyInputModule,
        MatLegacySelectModule,
        TellerOnlineIconsModule,
        NgxMaskDirective,
        NgxMaskPipe
    ],
    declarations: [BillingInfoComponent],
    exports: [BillingInfoComponent],
    providers: [provideNgxMask()]
})
export class BillingInfoModule { }

/** List of required fields that can be customized --
 * any not in this list (e.g. email and zip) always are required
 * The values of these must map to the BILLING_INFO_FIELDS above*/
export enum BillingInfoFields {
    phone = 'phone',
    email = 'email',
    addressCity = 'addressCity',
    addressState = 'addressState',
    addressRegion = 'addressRegion',
    addressCountry = 'addressCountry',
    addressLine1 = 'addressLine1',
    addressLine2 = 'addressLine2',
    addressZip = 'addressZip'
}
